import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { RadixSelect } from '@/components/radix';
import { t } from 'i18next';
import { DURATION_OPERATORS_WITHIN_AFTER } from '@/data/Marketing/marketingDurationOperators';
import DaySelector, { type DaySelectorLabel } from './DaySelector';
import {
    buildCompositeFromDaySelectorValue,
    DURATION_HOURS_TOUCHED,
    getDaySelectorValueFromComposite,
} from './durationPeriodUtils';
import { useGetCampaigns } from '@/hooks/api/marketing/useGetCampaigns';
import { GetApiCampaignsCampaignStatusItem } from '@/shared/api/models/getApiCampaignsCampaignStatusItem';
import { usePlaygroundFormRegistry } from '../../hooks/usePlaygroundFormRegistry';

interface CommunicationCompositeValues {
    campaignId?: string;
    interactionStatus?: string[];
    interactionField?: string;
    days?: [number | null, number | null];
    selectedDurationFormat?: DaySelectorLabel;
}

interface CommunicationInputProps {
    value?: string | string[] | null;
    compositeValues?: CommunicationCompositeValues;
    conditionInstanceId: string;
}

// Interaction status options
const interactionStatusOptions = [
    { label: t('Marketing.EmailCampaignsSent'), value: 'SENT', field: 'sent_at' },
    { label: t('Marketing.Opened'), value: 'OPENED', field: 'email_opened_at' },
    { label: t('Marketing.NotOpened'), value: 'NOT_OPENED', field: 'sent_at' },
    { label: t('Marketing.Clicked'), value: 'CLICKED', field: 'email_clicked_at' },
];

const CommunicationInput = ({ value, compositeValues, conditionInstanceId }: CommunicationInputProps) => {
    const { register } = usePlaygroundFormRegistry();
    // Fetch campaigns with limit 10000, excluding DRAFT and SCHEDULED statuses via API filter
    const { campaigns, isLoading: campaignsLoading } = useGetCampaigns({
        params: {
            page: 1,
            limit: 10000,
            campaign_status: [
                GetApiCampaignsCampaignStatusItem.TRIGGERED,
                GetApiCampaignsCampaignStatusItem.COMPLETED,
                GetApiCampaignsCampaignStatusItem.FAILED,
                GetApiCampaignsCampaignStatusItem.PAUSED,
                GetApiCampaignsCampaignStatusItem.CANCELLED,
            ],
        },
    });

    // Transform campaigns to options (already filtered by API)
    const campaignOptions =
        campaigns?.map((campaign) => ({
            label: campaign.name,
            value: campaign.id,
        })) || [];

    // Extract values from compositeValues - only handle array format
    const campaignId = compositeValues?.campaignId || (typeof value === 'string' ? value : null);
    const interactionStatusArray = compositeValues?.interactionStatus || (Array.isArray(value) ? value : []);
    // For single select, use first item from array
    const interactionStatus = interactionStatusArray.length > 0 ? interactionStatusArray[0] : '';
    const initialDuration = getDaySelectorValueFromComposite(compositeValues, {
        includeOperator: true,
        defaultOperator: 'AFTER_HOURS',
    });

    const formik = useFormik({
        initialValues: {
            campaignId: campaignId,
            interactionStatus: interactionStatus || '',
            duration: initialDuration,
        },
        validationSchema: Yup.object().shape({
            campaignId: Yup.string()
                .required(t('Marketing.CampaignRequired'))
                .typeError(t('Marketing.CampaignRequired')),
            interactionStatus: Yup.string()
                .oneOf(['SENT', 'OPENED', 'NOT_OPENED', 'CLICKED'], t('Marketing.InteractionStatusRequired'))
                .required(t('Marketing.InteractionStatusRequired')),
            duration: Yup.object().shape({
                hours: Yup.number().test(
                    'required',
                    t('Marketing.DaysRequired'),
                    (val) => val !== null && val !== undefined,
                ),
            }),
        }),
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: () => {
            // Validation handled automatically
        },
    });

    useEffect(() => {
        register(conditionInstanceId, {
            validate: async () => {
                if (campaignOptions.length === 0 && !campaignsLoading) return false;
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                    formik.setTouched({
                        campaignId: true,
                        interactionStatus: true,
                        duration: DURATION_HOURS_TOUCHED,
                    });
                    return false;
                }
                return true;
            },
            getValues: () => {
                if (campaignOptions.length === 0 && !campaignsLoading) {
                    return {
                        campaignId: null,
                        interactionStatus: [],
                        interactionField: null,
                        ...buildCompositeFromDaySelectorValue({ hours: 7, label: 'DAYS' }),
                    };
                }
                const selectedOption = interactionStatusOptions.find(
                    (option) => option.value === formik.values.interactionStatus,
                );
                const interactionField = selectedOption?.field || null;
                return {
                    campaignId: formik.values.campaignId,
                    interactionStatus: formik.values.interactionStatus ? [formik.values.interactionStatus] : [],
                    interactionField: interactionField,
                    ...buildCompositeFromDaySelectorValue(formik.values.duration),
                };
            },
        });
        // Keep validator in registry on unmount so we can validate all at once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conditionInstanceId, formik.values, campaignOptions.length, campaignsLoading, register]);

    const handleCampaignChange = (val: string) => {
        formik.setFieldValue('campaignId', val);
    };

    const handleInteractionStatusChange = (val: string) => {
        formik.setFieldValue('interactionStatus', val);
    };

    // Show message if no campaigns available
    if (!campaignsLoading && campaignOptions.length === 0) {
        return (
            <div className="flex flex-col gap-4 space-y-4">
                <div className="w-full p-4 bg-warning-50 border border-warning-200 rounded-md">
                    <p className="text-sm text-warning-700">{t('Marketing.NoActivatedTriggersOnOutlet')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {/* Campaign Select */}
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('Marketing.Trigger')}</label>
                <RadixSelect
                    options={campaignOptions}
                    value={formik.values.campaignId || undefined}
                    onValueChange={handleCampaignChange}
                    placeholder={t('Marketing.SelectTrigger')}
                    className="w-full"
                    disabled={campaignsLoading}
                />
                {formik.touched.campaignId && formik.errors.campaignId && (
                    <div className="text-sm text-red-500 mt-1">
                        {typeof formik.errors.campaignId === 'string'
                            ? formik.errors.campaignId
                            : t('Marketing.CampaignRequired')}
                    </div>
                )}
            </div>

            {/* Interaction Status Select */}
            <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Marketing.InteractionStatus')}
                </label>
                <RadixSelect
                    options={interactionStatusOptions.map((option) => ({
                        label: option.label,
                        value: option.value,
                    }))}
                    value={formik.values.interactionStatus || undefined}
                    onValueChange={handleInteractionStatusChange}
                    placeholder={t('Common.Select')}
                    className="w-full"
                />
                {formik.touched.interactionStatus && formik.errors.interactionStatus && (
                    <div className="text-sm text-red-500 mt-1">
                        {typeof formik.errors.interactionStatus === 'string'
                            ? formik.errors.interactionStatus
                            : t('Marketing.InteractionStatusRequired')}
                    </div>
                )}
            </div>

            <DaySelector
                label={t('Marketing.SelectTimePeriod')}
                showOperator
                operatorOptions={DURATION_OPERATORS_WITHIN_AFTER}
                value={formik.values.duration}
                onChange={(next) => formik.setFieldValue('duration', next)}
                summaryNoteKey="Marketing.DaySelectorNoteCommunication"
                summaryParams={{
                    interactionStatus: (
                        interactionStatusOptions
                            .find((option) => option.value === formik.values.interactionStatus)?.label ??
                        t('Marketing.InteractionStatus')
                    ).toLowerCase(),
                    triggerName:
                        campaignOptions.find((c) => c.value === formik.values.campaignId)?.label ??
                        t('Marketing.Trigger'),
                }}
                showError={Boolean(
                    formik.touched.duration &&
                        (formik.errors.duration as { hours?: string } | undefined)?.hours,
                )}
                error={
                    typeof (formik.errors.duration as { hours?: string } | undefined)?.hours === 'string'
                        ? (formik.errors.duration as { hours?: string }).hours
                        : t('Marketing.DaysRequired')
                }
            />
        </div>
    );
};

export default CommunicationInput;
